import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaSearch } from 'react-icons/fa';

const SearchableDropdown = ({
    options = [],
    value,
    onChange,
    placeholder = "Select...",
    labelKey = "name",
    valueKey = "id", // Key for the return value
    idKey = null, // Key for React unique identifier (defaults to valueKey)
    subLabelKey = null, // e.g., 'department'
    disabled = false,
    disabledOptions = new Set(),
    required = false,
    hideOnSearch = null // Function to determine if an option should be hidden during search
}) => {
    const uniqueKey = idKey || valueKey;
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!Array.isArray(options)) return;
        const selected = options.find(opt => opt[valueKey] === value);
        setSelectedOption(selected || null);
    }, [value, options, valueKey]);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
        }
    }, [isOpen]);

    // Handle outside click to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const disabledOptionsKey = Array.from(disabledOptions || []).sort().join(',');

    useEffect(() => {
        if (!Array.isArray(options)) return;
        if (!searchTerm) {
            setFilteredOptions(options.filter(opt => !disabledOptions.has(opt[valueKey])).slice(0, 5));
        } else {
            const lowerSearch = searchTerm.toLowerCase();
            const filtered = options.filter(opt => {
                if (disabledOptions.has(opt[valueKey])) return false;
                if (hideOnSearch && hideOnSearch(opt)) return false;
                const label = opt[labelKey]?.toString().toLowerCase() || "";
                const subLabel = subLabelKey && opt[subLabelKey] ? opt[subLabelKey].toString().toLowerCase() : "";
                return label.includes(lowerSearch) || subLabel.includes(lowerSearch);
            });
            setFilteredOptions(filtered.slice(0, 5));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, options, disabledOptionsKey, valueKey, labelKey, subLabelKey, hideOnSearch, uniqueKey]);

    const handleSelect = (option) => {
        const syntheticEvent = {
            target: {
                value: option[valueKey]
            }
        };
        onChange(syntheticEvent);
        setIsOpen(false);
        setSearchTerm("");
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full px-4 py-3 rounded-xl border flex justify-between items-center bg-gray-50 text-gray-800 cursor-pointer transition-all duration-200 ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-200' : 'hover:border-[#b7202e]'} ${isOpen ? 'ring-2 ring-[#b7202e]/20 border-[#b7202e]' : 'border-gray-300'}`}
            >
                <div className="flex-1 truncate">
                    {selectedOption ? (
                        <span>
                            {selectedOption[labelKey]}
                            {subLabelKey && selectedOption[subLabelKey] && ` (${selectedOption[subLabelKey]})`}
                        </span>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
                <FaChevronDown className={`text-gray-400 text-sm transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
            </div>

            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-fade-in-down">
                    <div className="p-2 border-b border-gray-100 bg-gray-50">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#b7202e] focus:ring-1 focus:ring-[#b7202e]"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>
                    <ul className="max-h-60 overflow-y-auto pl-0">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <li
                                    key={option[uniqueKey]}
                                    onClick={() => handleSelect(option)}
                                    className={`px-4 py-3 text-sm cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${value === option[valueKey] ? 'bg-[#b7202e]/5 text-[#b7202e] font-medium' : 'text-gray-700'}`}
                                >
                                    {option[labelKey]}
                                    {subLabelKey && option[subLabelKey] && (
                                        <span className="text-xs text-gray-500 ml-1">({option[subLabelKey]})</span>
                                    )}
                                </li>
                            ))
                        ) : (
                            <li className="px-4 py-3 text-sm text-center text-gray-400 italic">
                                No results found
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
